/**
 * HomeClient Component (Client-side UI only)
 *
 * This component receives all Strapi data as props from the Server Component parent (see app/page.tsx).
 * It is responsible for rendering the interactive UI and analytics attributes.
 * Theme and favicon management is handled app-wide by ThemeAndFavicon component in layout.tsx.
 *
 * Media URLs coming back from Strapi are rewritten to use the Next.js media proxy (/api/media/...) so the browser never contacts Strapi directly.
 * All data fetching is performed server-side; this component does not fetch from Strapi or use any env variables.
 */
"use client";

import { useState, FormEvent } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { GlobalData, ServicesData, StatsPageData } from '@/lib/strapi/types';
import { COMPANY_NAME } from '@/lib/constants';
import HamburgerMenu from './HamburgerMenu';
import Navbar from './Navbar';
import Card from './Card';

export type HomeClientProps = {
  global: GlobalData | null;
  services: ServicesData | null;
  stats: StatsPageData | null;
};

/**
 * Converts a Strapi media URL to a proxied media path.
 * 
 * This function takes a URL (absolute or relative) and transforms it into a proxied path
 * if it points to a Strapi upload. Only URLs with the `/uploads/` path segment are proxied.
 * 
 * @param url - The original media URL to be converted. Can be an absolute URL (http/https),
 *              a relative path, or undefined.
 * 
 * @returns The proxied media path in the format `/api/media/uploads/...` if the URL is valid
 *          and points to a Strapi upload, otherwise returns `undefined`.
 * 
 * @example
 * ```typescript
 * toMediaProxyPath('https://example.com/uploads/image.jpg')
 * // Returns: '/api/media/uploads/image.jpg'
 * 
 * toMediaProxyPath('/uploads/image.jpg')
 * // Returns: '/api/media/uploads/image.jpg'
 * 
 * toMediaProxyPath('https://example.com/other/image.jpg')
 * // Returns: undefined
 * 
 * toMediaProxyPath(undefined)
 * // Returns: undefined
 * ```
 */
function toMediaProxyPath(url?: string): string | undefined {
  if (!url) return undefined;
  try {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const u = new URL(url);
      // Only proxy Strapi uploads
      return u.pathname.startsWith('/uploads/') ? `/api/media${u.pathname}` : undefined;
    }
    return url.startsWith('/uploads/') ? `/api/media${url}` : undefined;
  } catch {
    return undefined;
  }
}

export default function HomeClient({ global, services, stats }: HomeClientProps) {
  // Contact form state
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState<string>('');

  // Derive media paths via proxy (fallback to local public assets)
  const logoLightSrc = toMediaProxyPath(global?.logoLight?.url) || '/MFJ-Associates-logo-light.svg';
  const logoDarkSrc = toMediaProxyPath(global?.logoDark?.url) || '/MFJ-Associates-logo-dark.svg';

  const address = global?.address;
  const cityRegionPostal = [address?.city, address?.state, address?.zipCode].filter(Boolean).join(', ');
  const companyAddress = [address?.streetAddress, address?.addressLine2, cityRegionPostal, address?.country]
    .filter(Boolean)
    .join(', ');

  // Helper for analytics attributes from Strapi
  const getAnalytics = (fieldName: string): Record<string, string> => {
    const key = `${fieldName}_analytics`;
    const dyn = (global as unknown as Record<string, unknown>) || {};
    const val = dyn[key] as Record<string, string> | undefined;
    return val || {};
  };

  // Contact form submission handler
  const handleContactSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormMessage('');

    // Store form reference before async operations
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = {
      fullName: formData.get('fullName') as string,
      email: formData.get('email') as string,
      company: formData.get('company') as string,
      message: formData.get('message') as string,
    };

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send message');
      }

      setFormStatus('success');
      setFormMessage(result.message || 'Thank you for your message!');
      form.reset();

      // Reset success message after 5 seconds
      setTimeout(() => {
        setFormStatus('idle');
        setFormMessage('');
      }, 5000);

    } catch (error) {
      setFormStatus('error');
      setFormMessage(error instanceof Error ? error.message : 'An error occurred. Please try again.');

      // Reset error message after 5 seconds
      setTimeout(() => {
        setFormStatus('idle');
        setFormMessage('');
      }, 5000);
    }
  };

  return (
    <>
      {/* Hamburger menu for mobile - ensure it's above nav bar */}
      <div className="fixed top-0 right-0 z-51 w-full pointer-events-none">
        <div className="pointer-events-auto">
          <HamburgerMenu menuItems={global?.menuItems || []} />
        </div>
      </div>

      <div className="min-h-screen font-sans" style={{ backgroundColor: 'var(--bg-primary)' }}>
        {/* Header Section */}
        <header className="header-hero relative py-20 text-center" style={{ background: 'var(--gradient-header)' }}>
          <div className="absolute inset-0 z-0" style={{ background: 'rgba(0, 0, 0, 0.2)' }}></div>

          {/* Large Logo */}
          <div className="header-logo-container relative z-20">
            <Image src={logoLightSrc} alt={global?.logoAlt || `${global?.companyName || 'Company'} logo`} width={150} height={120} className="logo logo-light header-logo" priority />
            <Image src={logoDarkSrc} alt={global?.logoAlt || `${global?.companyName || 'Company'} logo`} width={150} height={120} className="logo logo-dark header-logo" priority />
          </div>

          <div className="header-content relative z-10 mx-auto px-5">
            <h1 className="text-6xl font-light mb-5 drop-shadow-lg" {...getAnalytics('companyName')}>
              {global?.companyName || COMPANY_NAME}
            </h1>
            <p className="text-xl font-light opacity-95 mb-8" {...getAnalytics('companyTagline')}>
              {global?.companyTagline || "Building innovative solutions for tomorrow’s challenges"}
            </p>
            <a
              href="#contact"
              className="inline-block px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 hover:-translate-y-0.5"
              data-analytics="hero-cta-click"
              data-testid="hero-get-started-btn"
              data-section="hero"
              style={{
                backgroundColor: 'var(--accent-primary)',
                color: 'var(--text-button)',
                boxShadow: '0 8px 20px rgba(45, 212, 191, 0.4)',
                border: '2px solid var(--accent-primary)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--accent-secondary)';
                e.currentTarget.style.borderColor = 'var(--accent-secondary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--accent-primary)';
                e.currentTarget.style.borderColor = 'var(--accent-primary)';
              }}
            >
              Get Started
            </a>
          </div>
        </header>
        {/* Navigation - Home page only, below hero */}
        <Navbar global={global} layout={false} />

        {/* Main Content */}
        <main className="py-16">
          <div className="max-w-6xl mx-auto px-5">
            {/* Services Section */}
            <section id="services" className="mb-20">
              <h2 className="text-center text-5xl font-light mb-5" {...(services?.pageTitle_analytics || {})}>
                {services?.pageTitle || 'Our Services'}
              </h2>
              <p className="text-center text-lg max-w-2xl mx-auto mb-12" style={{ color: 'var(--text-secondary)' }} {...(services?.heading_analytics || {})}>
                {services?.heading ||
                  "We provide comprehensive solutions to help your business grow and succeed in today's competitive market."}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
                {(services?.services || [
                  {
                    icon: 'fas fa-code',
                    title: 'Web Development',
                    text: 'Custom web applications built with modern technologies. From simple websites to complex enterprise solutions.',
                  },
                  {
                    icon: 'fas fa-mobile-alt',
                    title: 'Mobile Solutions',
                    text: 'Native and cross-platform mobile applications that provide seamless user experiences across all devices.',
                  },
                  {
                    icon: 'fas fa-cloud',
                    title: 'Cloud Services',
                    text: 'Scalable cloud infrastructure and deployment solutions to ensure your applications run smoothly and efficiently.',
                  },
                ]).map((service, index) => {
                  const borderColors = ['var(--accent-primary)', 'var(--accent-secondary)', 'var(--accent-tertiary)'];
                  const borderColor = borderColors[index % borderColors.length];

                  return (
                    <Card
                      key={index}
                      className="p-10 rounded-2xl text-center"
                      borderTopColor={borderColor}
                      borderTopWidth="4px"
                    >
                      <div className="text-5xl mb-5" style={{ color: 'var(--accent-primary)' }} {...(service.icon_analytics || {})}>
                        <i className={'fa-solid ' + (service.icon || 'fa-cog')}></i>
                      </div>

                      <h3 className="text-2xl font-semibold mb-4" {...(service.title_analytics || {})}>
                        {service.title}
                      </h3>

                      <p className="leading-relaxed" {...(service.text_analytics || {})}>
                        {service.text}
                      </p>
                    </Card>
                  );
                })}
              </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 -mx-5 text-center" style={{ background: 'var(--gradient-stats)' }}>
              <div className="max-w-6xl mx-auto px-5">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
                  {stats?.projects && (
                    <div>
                      <h3 className="text-5xl font-bold mb-2">{stats.projects.value}</h3>
                      <p className="text-lg opacity-90">{stats.projects.description}</p>
                    </div>
                  )}
                  {stats?.clients && (
                    <div>
                      <h3 className="text-5xl font-bold mb-2">{stats.clients.value}</h3>
                      <p className="text-lg opacity-90">{stats.clients.description}</p>
                    </div>
                  )}
                  {stats?.years && (
                    <div>
                      <h3 className="text-5xl font-bold mb-2">{stats.years.value}</h3>
                      <p className="text-lg opacity-90">{stats.years.description}</p>
                    </div>
                  )}
                  {stats?.industries && (
                    <div>
                      <h3 className="text-5xl font-bold mb-2">{stats.industries.value}</h3>
                      <p className="text-lg opacity-90">{stats.industries.description}</p>
                    </div>
                  )}

                  {!stats && (
                    <>
                      <div>
                        <h3 className="text-5xl font-bold mb-2">100+</h3>
                        <p className="text-lg opacity-90">Projects Completed</p>
                      </div>
                      <div>
                        <h3 className="text-5xl font-bold mb-2">50+</h3>
                        <p className="text-lg opacity-90">Happy Clients</p>
                      </div>
                      <div>
                        <h3 className="text-5xl font-bold mb-2">5+</h3>
                        <p className="text-lg opacity-90">Years Experience</p>
                      </div>
                      <div>
                        <h3 className="text-5xl font-bold mb-2">24/7</h3>
                        <p className="text-lg opacity-90">Support Available</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </section>

            {/* About Section */}
            <section id="about" className="py-20 -mx-5" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
              <div className="max-w-6xl mx-auto px-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                  <div>
                    <h2 className="text-5xl font-light mb-5" {...getAnalytics('companyName')}>
                      About {global?.companyName || COMPANY_NAME}
                    </h2>
                    <div className="space-y-5 text-lg leading-relaxed">
                      <p {...getAnalytics('companyDescription')}>
                        {global?.companyDescription ||
                          'We are a forward-thinking technology company dedicated to delivering innovative solutions that drive business success. With years of experience in the industry, we combine technical expertise with creative problem-solving.'}
                      </p>
                      <p>
                        Our team of skilled professionals works closely with clients to understand their unique needs and deliver customized solutions that exceed expectations. We pride ourselves on quality, reliability, and exceptional customer service.
                      </p>
                      <p>
                        From startups to enterprise-level organizations, we have the experience and expertise to help you achieve your digital transformation goals.
                      </p>
                      <p>
                        {companyAddress
                          ? `Located at ${companyAddress}.`
                          : 'Located at our office location listed in our CMS profile.'}
                      </p>
                    </div>
                  </div>

                  <Link href="/profiles" passHref legacyBehavior>
                    <a
                      className="h-96 rounded-2xl flex items-center justify-center text-xl text-center border-2 cursor-pointer transition-all duration-300 hover:bg-white/10 hover:scale-105"
                      style={{
                        background: 'var(--gradient-about)',
                        borderColor: 'var(--border-accent)',
                        textDecoration: 'none',
                      }}
                      aria-label="View all profiles"
                    >
                      <div>
                        <i className="fas fa-users text-6xl mb-5"></i>
                        <br />
                        Expert Team
                        <br />
                        Ready to Help
                      </div>
                    </a>
                  </Link>
                </div>
              </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-20 text-center -mx-5" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
              <div className="max-w-6xl mx-auto px-5">
                <h2 className="text-5xl font-light mb-5">
                  Get In Touch
                </h2>
                <p className="text-lg max-w-2xl mx-auto mb-12">
                  Ready to start your next project? Contact us today and let&apos;s discuss how we can help you achieve your goals.
                </p>

                <form className="max-w-2xl mx-auto" onSubmit={handleContactSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                    <div className="text-left">
                      <label htmlFor="fullName" className="block mb-1 font-medium">
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        required
                        disabled={formStatus === 'submitting'}
                        className="w-full p-4 text-base rounded-lg border-2 transition-all duration-300 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          backgroundColor: 'var(--bg-input)',
                          borderColor: 'var(--border-primary)',
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = 'var(--border-accent)';
                          e.currentTarget.style.boxShadow = '0 0 10px rgba(45, 212, 191, 0.3)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'var(--border-primary)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      />
                    </div>

                    <div className="text-left">
                      <label htmlFor="email" className="block mb-1 font-medium">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        disabled={formStatus === 'submitting'}
                        className="w-full p-4 text-base rounded-lg border-2 transition-all duration-300 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          backgroundColor: 'var(--bg-input)',
                          borderColor: 'var(--border-primary)',
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = 'var(--border-accent)';
                          e.currentTarget.style.boxShadow = '0 0 10px rgba(45, 212, 191, 0.3)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'var(--border-primary)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      />
                    </div>
                  </div>

                  <div className="mb-5 text-left">
                    <label htmlFor="company" className="block mb-1 font-medium">
                      Company (Optional)
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      disabled={formStatus === 'submitting'}
                      className="w-full p-4 text-base rounded-lg border-2 transition-all duration-300 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: 'var(--bg-input)',
                        borderColor: 'var(--border-primary)',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border-accent)';
                        e.currentTarget.style.boxShadow = '0 0 10px rgba(45, 212, 191, 0.3)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border-primary)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  <div className="mb-5 text-left">
                    <label htmlFor="message" className="block mb-1 font-medium">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      required
                      disabled={formStatus === 'submitting'}
                      className="w-full p-4 text-base rounded-lg border-2 transition-all duration-300 focus:outline-none resize-vertical disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: 'var(--bg-input)',
                        borderColor: 'var(--border-primary)',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border-accent)';
                        e.currentTarget.style.boxShadow = '0 0 10px rgba(45, 212, 191, 0.3)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border-primary)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Status Message */}
                  {formMessage && (
                    <div 
                      className="mb-5 p-4 rounded-lg text-center font-medium"
                      style={{
                        backgroundColor: formStatus === 'success' ? 'var(--bg-success)' : 'var(--bg-error)',
                        color: formStatus === 'success' ? 'var(--text-success)' : 'var(--text-error)',
                      }}
                    >
                      {formMessage}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="px-10 py-4 rounded-full text-lg font-semibold transition-all duration-300 hover:scale-105 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:translate-y-0"
                    style={{
                      backgroundColor: 'var(--accent-primary)',
                      color: 'var(--text-button)',
                      boxShadow: '0 8px 20px rgba(45, 212, 191, 0.4)',
                    }}
                    onMouseEnter={(e) => {
                      if (formStatus !== 'submitting') {
                        e.currentTarget.style.backgroundColor = 'var(--accent-secondary)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--accent-primary)';
                    }}
                  >
                    {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </section>
          </div>
        </main>
      </div>
    </>
  );
}
